/**
 * ConditionBuilder - AND-only predicate editor.
 *
 * Renders a flat list of <ConditionRow> with an AND chip between each
 * row and an "Add condition" button below. The builder knows nothing
 * about the domain: all refs are supplied by the parent via
 * `availableRefs`, so S4/S5 can reuse it by passing their own lists.
 *
 * Public contract:
 *   - `value`       - Predicate JSON (as persisted by the backend) or null.
 *   - `onChange`    - called with the next Predicate (or null when empty).
 *   - `availableRefs` / `byRef` - ref catalog + lookup, typically from
 *     `useAvailableRefs()`.
 */

import { useCallback, useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { ConditionRow } from './condition-row'
import { buildFallbackRef } from './fallback-ref'
import {
  builderToPredicate,
  emptyClause,
  predicateToBuilder,
  type AvailableRef,
  type ClauseNode,
  type PredicateNode,
} from './types'

interface ConditionBuilderProps {
  value: PredicateNode | null | undefined
  onChange: (next: PredicateNode | null) => void
  availableRefs: AvailableRef[]
  byRef: (ref: string) => AvailableRef | undefined
  disabled?: boolean
}

export function ConditionBuilder({
  value,
  onChange,
  availableRefs,
  byRef,
  disabled,
}: ConditionBuilderProps) {
  // Internal draft state - keeps blank rows visible while the user edits.
  // The parent only ever sees the serialised predicate (blanks filtered out).
  const [clauses, setClauses] = useState<ClauseNode[]>(() => {
    const seeded = predicateToBuilder(value ?? null).clauses
    return seeded.length ? seeded : [emptyClause()]
  })
  const initialKey = JSON.stringify(value ?? null)
  const [lastSeen, setLastSeen] = useState<string>(initialKey)
  const [lastEmitted, setLastEmitted] = useState<string>(initialKey)

  // Resync from `value` during render when the parent supplies a new
  // predicate (e.g. the dialog opening with a different rule).  Skipped
  // when `value` just echoes what we emitted ourselves.
  const incoming = JSON.stringify(value ?? null)
  if (incoming !== lastSeen) {
    setLastSeen(incoming)
    if (incoming !== lastEmitted) {
      setLastEmitted(incoming)
      const seeded = predicateToBuilder(value ?? null).clauses
      setClauses(seeded.length ? seeded : [emptyClause()])
    }
  }

  const emit = useCallback(
    (next: ClauseNode[]) => {
      setClauses(next)
      const predicate = builderToPredicate({ clauses: next })
      const serialized = JSON.stringify(predicate)
      setLastEmitted(serialized)
      setLastSeen(serialized)
      onChange(predicate)
    },
    [onChange],
  )

  const updateAt = (index: number, next: ClauseNode) => {
    const copy = [...clauses]
    copy[index] = next
    emit(copy)
  }

  const removeAt = (index: number) => {
    if (clauses.length <= 1) {
      emit([emptyClause()])
      return
    }
    emit(clauses.filter((_, i) => i !== index))
  }

  const addClause = () => emit([...clauses, emptyClause()])

  // Surface any clause ref that the parent catalog didn't enumerate (e.g.
  // seed-rule `computed:date_diff(...)` refs) as a synthetic entry so the
  // SearchableSelect renders a readable label instead of falling through
  // to the "Select a field…" placeholder, which makes AND groups with
  // seed-authored clauses look blank after round-tripping.
  const { augmentedRefs, augmentedByRef } = useMemo(() => {
    const known = new Set(availableRefs.map((r) => r.ref))
    const extras: AvailableRef[] = []
    const extraMap = new Map<string, AvailableRef>()
    for (const c of clauses) {
      if (!c.ref || known.has(c.ref) || extraMap.has(c.ref)) continue
      const fallback = buildFallbackRef(c.ref)
      extras.push(fallback)
      extraMap.set(c.ref, fallback)
    }
    const merged = extras.length ? [...availableRefs, ...extras] : availableRefs
    const lookup = (ref: string): AvailableRef | undefined =>
      byRef(ref) ?? extraMap.get(ref)
    return { augmentedRefs: merged, augmentedByRef: lookup }
  }, [availableRefs, byRef, clauses])

  return (
    <div
      data-testid="condition-builder"
      className="flex flex-col gap-2"
      aria-disabled={disabled || undefined}
    >
      {clauses.map((clause, index) => (
        <div key={index} className="flex flex-col gap-2">
          <ConditionRow
            clause={clause}
            availableRefs={augmentedRefs}
            byRef={augmentedByRef}
            onChange={(next) => updateAt(index, next)}
            onRemove={() => removeAt(index)}
            canRemove={clauses.length > 1}
          />
          {index < clauses.length - 1 && (
            <div className="flex items-center gap-2 pl-1">
              <span className="inline-flex h-5 items-center rounded-full border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-[var(--ink-3)]">
                and
              </span>
              <span className="h-px flex-1 bg-[var(--rule)]" />
            </div>
          )}
        </div>
      ))}

      <div className="pt-1">
        <Button
          type="button"
          variant="outline"
          size="xs"
          onClick={addClause}
          disabled={disabled}
          data-testid="condition-add"
          className="text-[12px]"
        >
          <Plus size={12} className="mr-1" />
          Add condition
        </Button>
      </div>
    </div>
  )
}

export type {
  AvailableRef,
  ClauseNode,
  GroupNode,
  Op,
  PredicateNode,
  RefCategory,
  RefType,
} from './types'
